// src/api/axiosInstance.ts

import axios, { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

// Define an interface for tokens stored in cookies
interface AuthTokens {
    access_token: string;
    refresh_token: string;
}

// Create an Axios instance
const instance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8080',
    timeout: 10000, // 요청 타임아웃 10초 설정
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Function to redirect to login
const redirectToLogin = () => {

};

// Response interceptor to handle token refreshing and error responses
instance.interceptors.response.use(
    (response: AxiosResponse) => response, // 응답이 성공적이면 그대로 반환
    async (error: AxiosError) => {
        if (!axios.isAxiosError(error)) {
            return Promise.reject(error);
        }

        const { config, response } = error;

        if (!config || !response) {
            // config나 response가 없으면 에러 반환
            return Promise.reject(error);
        }

        const originalRequest = config as AxiosRequestConfig;

        // 로그인 요청은 인터셉터에서 처리하지 않도록 예외 처리
        const isLoginRequest =
            originalRequest.url?.includes('/auth/users/login') ||
            originalRequest.url?.includes('/auth/users/register');

        // 무한 루프를 방지하기 위하여 _retry 플래그 확인
        if ((response.status === 401 || response.status === 403) && !originalRequest._retry) {
            originalRequest._retry = true; // 재시도 플래그 설정

            if (!isLoginRequest) {
                // 401 Unauthorized 에러 처리
                if (response.status === 401) {
                    try {
                        const tokensString = Cookies.get('auth'); // 쿠키에서 auth 토큰 가져오기
                        if (!tokensString) {
                            // 토큰이 없으면 로그인 페이지로 리디렉션
                            redirectToLogin();
                            return Promise.reject(error);
                        }

                        const tokens: AuthTokens = JSON.parse(tokensString);
                        const { refresh_token } = tokens;

                        if (!refresh_token) {
                            // refresh_token이 없으면 로그인 페이지로 리디렉션
                            redirectToLogin();
                            return Promise.reject(error);
                        }

                        // access_token 갱신 시도
                        const res = await axios.post(
                            'http://127.0.0.1:8080/api/auth/token/refresh', // 토큰 갱신 엔드포인트
                            { refresh_token },
                            {
                                headers: { 'Content-Type': 'application/json' },
                                withCredentials: true,
                            }
                        );

                        const { access_token: new_access_token } = res.data;

                        if (new_access_token) {
                            // 쿠키에 새 access_token 저장 (refresh_token 보존)
                            Cookies.set('auth', JSON.stringify({ refresh_token, access_token: new_access_token }), { expires: 7, secure: true, sameSite: 'Lax' });

                            // 원래 요청의 Authorization 헤더 업데이트
                            if (originalRequest.headers) {
                                originalRequest.headers.Authorization = `Bearer ${new_access_token}`;
                            }

                            // 새 access_token으로 원래 요청 재시도
                            return instance(originalRequest);
                        }

                        // 새 access_token이 없으면 로그인 페이지로 리디렉션
                        redirectToLogin();
                        return Promise.reject(error);
                    } catch (refreshError) {
                        console.error('Token refresh failed:', refreshError);
                        // 토큰 갱신 실패 시 auth 쿠키 제거 및 로그인 페이지로 리디렉션
                        Cookies.remove('auth');
                        redirectToLogin();
                        return Promise.reject(refreshError);
                    }
                }

                // 403 Forbidden 에러 처리 (선택 사항)
                if (response.status === 403) {
                    // Authorization 헤더 제거
                    if (originalRequest.headers) {
                        delete originalRequest.headers.Authorization;
                    }

                    // 로그인 페이지로 리디렉션
                    redirectToLogin();

                    return Promise.reject(error);
                }
            }
        }

        // 기타 에러는 그대로 반환
        return Promise.reject(error);
    }
);

export { instance };